<template>
  <div class="form-section group">
    <span class="icon"><icon-colored-c-p-m /></span>
    {{ capitalize(i(isCuratorOrganization ? 'names.pricing' : 'names.cpm', 1)) }}
    <div class="actions">
      <div
        v-show="!isCuratorOrganization && !isNGExternalOrPreferredExternalDeal"
        class="toggle">
        <span data-entity-type="cpm-per-rate-card">{{ capitalize(i('names.perRateCard', 1)) }}</span>
        <ToggleButton
          id="edit_package_cpm_toggle"
          class="ml-2 mr-2"
          :value="!isCPMFromRateCard"
          @change="emits('update:isCPMFromRateCard')" />
        <span data-entity-type="cpm-predefined">{{ capitalize(i('states.predefined')) }}</span>
      </div>
    </div>
  </div>
  <div
    v-if="isCuratorOrganization || isNGExternalOrPreferredExternalDeal || !isCPMFromRateCard"
    class="form-section-content">
    <div
      v-if="isCuratorOrganization || isNGExternalOrPreferredExternalDeal"
      class="form-group row">
      <form-label :is-required="true">{{ capitalize(i('names.auctionType', 1)) }}</form-label>
      <div class="col-sm-5 deliver-on-tabs-slider">
        <tabs-slider
          width="100%"
          height="4rem"
          :selected-tab-i-d="auctionType"
          :is-disabled="!canEditAuctionType"
          :tabs="auctionOptions"
          @selected-tab-changed="(val) => emits('update:auctionType', val.id)" />
      </div>
      <span class="col-sm-1 field-help">
        <span v-tooltip="capitalize(i('messages.auctionTypeInfoTooltip'))">
          <icon-info />
        </span>
      </span>
    </div>
    <div
      v-show="isCuratorOrganization || isNGExternalOrPreferredExternalDeal"
      class="form-group row">
      <form-label :is-required="true">{{ capitalize(i('messages.floorPrice')) }}</form-label>
      <div class="col-sm-5 floor-price-tabs-slider">
        <tabs-slider
          width="100%"
          height="4rem"
          :selected-tab-i-d="pricingType"
          :tabs="pricingOptions"
          @selected-tab-changed="(val) => emits('update:pricingType', val.id)" />
      </div>
      <span class="col-sm-1 field-help">
        <span v-tooltip="capitalize(i('messages.floorPriceInfoTooltip'))">
          <icon-info />
        </span>
      </span>
    </div>
    <div
      v-show="!isCPMFromRateCard"
      class="form-group row">
      <form-label :is-required="true">
        <span>
          {{ capitalize(i('names.cpm', 1)) }}
        </span>
      </form-label>
      <div class="col-sm-5">
        <div class="input-group mb-2 mr-sm-2 mb-sm-0">
          <formatted-input
            id="edit_package_cpm_fixed"
            name="cpmFixed"
            :is-money="true"
            :custom-currency-symbol="currencySymbol"
            :value="cpmFixed"
            :disabled="!canEditCPMFixed"
            :validation-errors="errors"
            @value-changed="(val) => emits('update:cpmFixed', val)" />
        </div>
        <span
          v-show="errors.has('cpmFixed')"
          class="help is-danger"
          >{{ errors.first('cpmFixed') }}</span
        >
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { capitalize, inject, Ref } from 'vue';
  import { useI18n } from 'vue-i18n';

  import { FormattedInput, FormLabel, TabsSlider } from '@/common/components/controls';
  import ToggleButton from '@/common/components/controls/ToggleButton.vue';
  import { IconColoredCPM } from '@/common/icons/colored';
  import { IconInfo } from '@/common/icons/monochrome';
  import { AuctionType } from '@/common/models/package/AuctionType.enum';
  import { Pricing } from '@/common/models/package/Pricing.enum';
  import { i } from '@/globals';
  import { mapOrgAppGetters } from '@/utils/StateMapping';
  import MessageCollection from '@/validator/MessageCollection';

  //#region Props
  interface IDealPricingFormProps {
    isNGExternalOrPreferredExternalDeal: boolean;
    isCPMFromRateCard: boolean;
    auctionType: AuctionType;
    pricingType: Pricing;
    canEditAuctionType: boolean;
    canEditCPMFixed: boolean;
    cpmFixed: number;
    currencySymbol: string;
  }

  const {
    isNGExternalOrPreferredExternalDeal,
    isCPMFromRateCard,
    auctionType,
    pricingType,
    canEditAuctionType,
    canEditCPMFixed,
    cpmFixed,
    currencySymbol,
  } = defineProps<IDealPricingFormProps>();
  //#endregion

  //#region Emits
  const emits = defineEmits([
    'update:isCPMFromRateCard',
    'update:auctionType',
    'update:pricingType',
    'update:cpmFixed',
  ]);
  //#endregion

  //#region Mappings/Getters
  const { isCuratorOrganization } = mapOrgAppGetters();
  //#endregion

  //#region Data
  const errors: Ref<MessageCollection> = inject('errors', null);
  const i18n = useI18n();
  const auctionOptions = [
    { id: AuctionType.FirstPrice, name: capitalize(i18n.t('states.firstPrice')) },
    { id: AuctionType.FixedPrice, name: capitalize(i18n.t('states.fixedPrice')) },
  ];
  const pricingOptions = [
    { id: Pricing.Static, name: capitalize(i18n.t('messages.static')) },
    { id: Pricing.RateCard, name: capitalize(i18n.t('messages.rateCard')) },
  ];
</script>

<style scoped lang="scss">
  @use '@/styles/variables' as *;
</style>
